import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import { logger } from '../logger.service';
import * as jose from 'jose';
import { Auth } from '../auth.service';
import { BusEvent, EBusEvent } from '../bus-event/bus-event.service';

interface MSALTokenArgs {
  instance: IPublicClientApplication;
  account: AccountInfo;
}

let refreshTokenTimer: NodeJS.Timeout | null = null;

const getMSALToken = ({
  instance,
  account,
}: MSALTokenArgs): Promise<string> => {
  const defScope = loginRequest();

  const request = {
    ...defScope,
    account,
  };
  return instance
    .acquireTokenSilent(request)
    .then((response) => response.accessToken)
    .catch(async () => {
      if (account) {
        instance.setActiveAccount(account);
        try {
          const retryResponse = await instance.acquireTokenSilent({
            ...request,
            account,
          });
          return retryResponse.accessToken;
        } catch (retryError) {
          logger.log('[MSALToken] Silent token acquisition failed');
          BusEvent.sendEvent({ event: EBusEvent.TOKEN_EXPIRED });
          throw retryError;
        }
      }

      const accounts = instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        logger.log('[MSALToken] No accounts found');
        BusEvent.sendEvent({ event: EBusEvent.TOKEN_EXPIRED });
        throw new Error('No accounts found for silent token acquisition');
      }
    });
};

export const setupRefreshTokenTimeOut = (callBack: () => Promise<void>) => {
  if (refreshTokenTimer) {
    clearTimeout(refreshTokenTimer);
    refreshTokenTimer = null;
  }

  const token = Auth.getDynJwtToken();

  const jwtString =
    typeof token === 'string'
      ? token
      : typeof (token as any)?.accessToken === 'string'
        ? (token as any).accessToken
        : typeof (token as any)?.idToken === 'string'
          ? (token as any).idToken
          : undefined;

  if (!jwtString) {
    logger.critical('[AUTH] JWT is not a string or missing');
    return;
  }

  const payload = jose.decodeJwt(jwtString) as { exp?: number };
  const time = payload.exp ?? 0;
  const expiredTime = time * 1000;
  const refreshTime = expiredTime - Date.now();

  logger.log(
    `[AUTH] Token will be refreshed after ${refreshTime / 1000 / 60} min`,
  );

  if (refreshTime < 0) {
    return;
  }

  refreshTokenTimer = setTimeout(() => {
    callBack();
  }, refreshTime);
};

export const chargeMSALTokenProcess = async ({
  instance,
  account,
}: MSALTokenArgs): Promise<string | undefined> => {
  const token = await getMSALToken({ instance, account });

  if (!token) {
    logger.critical(
      `[AUTH] Can't get token in MSAL service, token is ${token}`,
    );
    BusEvent.sendEvent({ event: EBusEvent.DYN_TOKEN_ERR });
    return undefined;
  }

  Auth.saveDynToken(token);
  Auth.saveMSALToken(token);
  return token;
};
